import { Injectable } from '@angular/core';
import { TLogApiRequest } from '@monorepo/types';
import { CustomLoggerDbService } from './custom-logger-db.service';
import debounce from 'lodash/debounce';
import { TAppDeviceInfo, AppConfig } from '@monorepo/types';
import { HttpClient } from '@angular/common/http';
import { STATELESS_UTIL } from './util';

const INTERVAL_TO_SYNC_LOGS = 30000;

@Injectable({
  providedIn: 'root',
})
export class CustomLoggerService<T extends TAppDeviceInfo = TAppDeviceInfo> {
  private deviceAppInfo: T | null = null;
  private appConfig: AppConfig | null = null;
  private logBuffer: TLogApiRequest[] = [];
  private flushLogBufferDebounced = debounce(() => this.flushLogBuffer(), 500);

  constructor(private dbService: CustomLoggerDbService, private http: HttpClient) {}

  log(message: string, ...additional: unknown[]) {
    // Make sure message is string when calling log
    this.#handleLog('LOG', message, additional);
  }

  info(messageOrError: string | Error, ...additional: unknown[]): void {
    this.#handleLog('INFO', messageOrError, additional);
  }

  warn(messageOrError: string | Error, ...additional: unknown[]): void {
    this.#handleLog('WARN', messageOrError, additional);
  }

  error(messageOrError: string | Error, ...additional: unknown[]): void {
    this.#handleLog('ERROR', messageOrError, additional);
  }

  logUnhandledError(messageOrError: string | Error, ...additional: unknown[]): void {
    try {
      const formatted = JSON.stringify(additional, null, 2); // Pretty print
      this.#handleLog('UNHANDLED_ERROR', messageOrError, [formatted]);
    } catch (error) {
      console.error('Error formatting additional data:', error);
    }
  }

  debug(messageOrError: string | Error, ...additional: unknown[]): void {
    this.#handleLog('DEBUG', messageOrError, additional);
  }

  fatal(messageOrError: string | Error, ...additional: unknown[]): void {
    this.#handleLog('FATAL', messageOrError, additional);
  }

  // 3. Public method to initialize device info
  public initializeLogger(config: AppConfig, deviceInfo: T): void {
    this.appConfig = config;
    this.deviceAppInfo = deviceInfo;
    this.#startScheduler();
  }

  public getAppConfig(): AppConfig | null {
    return this.appConfig;
  }

  // 5. Transformer: convert log/device data to required API format
  #startScheduler() {
    // this will sync log at the time of startScheduler execution
    if (navigator.onLine) {
      this.#flushLogs();
    }
    // this will schedule log sync at interval 1 min interval
    setInterval(() => {
      if (navigator.onLine) {
        this.#flushLogs();
      }
    }, INTERVAL_TO_SYNC_LOGS);
  }
  // navigator.onLine is not 100% reliable, 
  // It returns true, If the device is connected to a device/router but the network is down.

  #createLogObject(level: TLogApiRequest['level'], message: unknown, additional: unknown[]): TLogApiRequest {
    if (!this.deviceAppInfo) {
      throw new Error('DeviceAppInfo must be initialized before logging.');
    }
    const timestamp = new Date();
    const origin = level === 'LOG' ? '' : STATELESS_UTIL.getLogOrigin();
    const logId = STATELESS_UTIL.generateLogId(level, message, additional, timestamp);
    return {
      ...this.deviceAppInfo,
      level,
      message: String(message),
      timestamp: timestamp.toString(),
      origin: origin,
      id: logId,
      additional: additional.map(STATELESS_UTIL.sanitizeForLogging),
    };
  }

  async #handleLog(level: TLogApiRequest['level'], message: string | Error, additional: unknown[]) {
    const logApiRequest: TLogApiRequest = this.#createLogObject(level, message, additional);
    this.#logByLevel(level, message, additional);
    // For 'LOG' level, avoid #getLogOrigin for performance, as the log method may be called repeatedly; message should be a string
    this.#addLogwithLevelFilter(logApiRequest);
    //In this structure, the log is being stored in IndexedDB
  }

  async #flushLogs(): Promise<void> {
    console.log('Flushing the logs');
    try {
      const { topK, isLogsRemaining } = await this.dbService.extractLogsToBeFlushed();
      if (topK.length === 0) return;
      this.syncLogsToApi(topK);
      if (isLogsRemaining) {
        // Recursively flush remaining logs
        await this.#flushLogs();
      }
    } catch (err) {
      console.error('Critical error during log flush:', err);
      await this.error('Critical error in #flushLogs:', err);
    }
  }

  async #handleError(logsToBeRepushed: TLogApiRequest[], error: any) {
    try {
      console.warn('Re-queuing logs due to error:', error);
      await this.dbService.addLogs(logsToBeRepushed); // Requeue logs to DB
      if (error?.status === 0) {
        // Skip adding a new log entry for network errors 
        return;
      } else {
        this.error(`Failed to flush logs:  HTTP status (${error?.status || 'Unknown'})`, error);
      }
    } catch (dbError) {
      this.error('Critical error: Unable to store logs in DB:', dbError);
    }
  }

  #logByLevel(level: TLogApiRequest['level'], message: string | Error, ...args: unknown[]) {
    const logDTO = ['[Custom logger log]', message, ...args];
    switch (level) {
      case 'DEBUG':
        console.debug(...logDTO);
        break;
      case 'INFO':
        console.info(...logDTO);
        break;
      case 'WARN':
        console.warn(...logDTO);
        break;
      case 'ERROR':
      case 'UNHANDLED_ERROR':
        console.error(...logDTO);
        break;
      case 'LOG':
        console.log(...logDTO);
        break;
      default:
        console.error(`Unknown log level: ${level}`);
    }
  }

  async #addLogwithLevelFilter(log: TLogApiRequest) {
    switch (log.level) {
      case 'ERROR':
      case 'FATAL':
      case 'LOG':
      case 'UNHANDLED_ERROR':
        this.logBuffer.push(log);
        this.flushLogBufferDebounced();
        break;
      default:
        // Ignore logs
        break;
    }
  }

  private async flushLogBuffer() {
    if (this.logBuffer.length === 0) return;
    const logsToWrite = [...this.logBuffer];
    this.logBuffer = [];
    await this.dbService.addLogs(logsToWrite);
  }
  /**
   * @deprecated
   * lets not do that, going against the original design
   * if want to keep it keep this is separate place
   */
  sendDirectLogsToBackend(message: string, ...additional: unknown[]) {
    const logApiRequest: TLogApiRequest = this.#createLogObject('LOG', message, additional);
    this.syncLogsToApi([logApiRequest]);
  }

  private syncLogsToApi(logs: TLogApiRequest[]) {
    try {
      const config = this.getAppConfig();
      if (!config) {
        console.error('No app config found for logger!');
        return;
      }
      this.http.post(config.logEndpoint, { logErrorsRequests: logs }, { responseType: 'text' }).subscribe({
        next: (response) => {
          console.log('Logs synced successfully:', response);
        },
        error: (err) => {
          this.#handleError(logs, err);
        },
      });
    } catch (err) {
      this.#handleError(logs, err);
    }
  }
}
